import { useState, useEffect } from 'react';

const COPA_INICIO = new Date('2026-06-11T21:00:00-03:00');
const EMBLEMA_URL = '/copa-2026.png';

interface Restante {
  dias: number; horas: number; minutos: number; segundos: number; total: number;
}

function calcularRestante(): Restante {
  const total = Math.max(0, COPA_INICIO.getTime() - Date.now());
  return {
    total,
    dias:     Math.floor(total / 86_400_000),
    horas:    Math.floor((total % 86_400_000) / 3_600_000),
    minutos:  Math.floor((total % 3_600_000)  /    60_000),
    segundos: Math.floor((total % 60_000)      /     1_000),
  };
}

// Contorno sólido negro em todas as direções — leitura garantida sobre qualquer fundo
const outline = (blur = 6) =>
  `-1px -1px ${blur}px rgba(0,0,0,1), 1px -1px ${blur}px rgba(0,0,0,1), -1px 1px ${blur}px rgba(0,0,0,1), 1px 1px ${blur}px rgba(0,0,0,1)`;

const glowAmarelo = `${outline(8)}, 0 0 22px rgba(255,223,0,0.75), 0 0 48px rgba(255,200,0,0.35)`;
const glowVerde   = `${outline(6)}, 0 0 14px rgba(0,180,60,0.6)`;
const glowBranco  = `${outline(5)}, 0 0 10px rgba(255,255,255,0.4)`;

interface Props { fixtureIdAtivo: number | null; }

/**
 * TvCopaCountdown — sem card. Gradiente radial oval cria "palco" para o conteúdo;
 * efeito neon nos números dá legibilidade sem fundo sólido.
 * Layout: emblema | linha | DIAS grande | linha | h:m:s
 */
export default function TvCopaCountdown({ fixtureIdAtivo }: Props) {
  const [restante, setRestante] = useState<Restante>(calcularRestante);
  const [imgErr,   setImgErr]   = useState(false);

  useEffect(() => {
    const t = setInterval(() => setRestante(calcularRestante()), 1_000);
    return () => clearInterval(t);
  }, []);

  if (fixtureIdAtivo || restante.total <= 0) return null;

  return (
    <div
      className="fixed pointer-events-none select-none animate-fade-in"
      style={{
        bottom:    0,
        left:      '50%',
        transform: 'translateX(-50%)',
        zIndex:    15,
        // Oval escuro que cria "palco" sem parecer um retângulo
        background: 'radial-gradient(ellipse 90% 85% at 50% 100%, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.28) 55%, transparent 100%)',
        padding:    'clamp(14px, 1.8vw, 30px) clamp(20px, 2.5vw, 48px) clamp(7px, 0.9vw, 15px)',
        display:    'flex',
        alignItems: 'center',
        gap:        'clamp(9px, 1.1vw, 19px)',
      }}
    >
      {/* ── Emblema FIFA ── */}
      {!imgErr ? (
        <img
          src={EMBLEMA_URL}
          alt="Copa do Mundo FIFA 2026"
          onError={() => setImgErr(true)}
          style={{
            height:   'clamp(30px, 3.3vw, 54px)',
            width:    'auto',
            filter:   'drop-shadow(0 0 14px rgba(0,0,0,0.95)) drop-shadow(0 0 6px rgba(0,0,0,1))',
            flexShrink: 0,
          }}
        />
      ) : (
        <span style={{ fontSize: 'clamp(18px, 2vw, 36px)', filter: 'drop-shadow(1px 1px 4px rgba(0,0,0,1))' }}>🏆</span>
      )}

      {/* Linha verde */}
      <div style={{ width: 'clamp(2px, 0.2vw, 3px)', height: 'clamp(26px, 2.8vw, 46px)', background: '#009C3B', opacity: 0.85, flexShrink: 0, borderRadius: 2 }} />

      {/* ── Dias — elemento dominante ── */}
      <div className="flex flex-col items-center" style={{ gap: 0 }}>
        <span
          className="font-black font-mono tabular-nums leading-none"
          style={{
            fontSize:   'clamp(34px, 3.9vw, 66px)',
            color:      '#FFDF00',
            textShadow: glowAmarelo,
            lineHeight: 0.88,
          }}
        >
          {String(restante.dias).padStart(2, '0')}
        </span>
        <span style={{
          fontSize:      'clamp(7px, 0.62vw, 11px)',
          color:         '#009C3B',
          fontWeight:    900,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          textShadow:    glowVerde,
          marginTop:     'clamp(2px, 0.3vw, 5px)',
        }}>
          dias
        </span>
      </div>

      {/* Linha verde */}
      <div style={{ width: 'clamp(2px, 0.2vw, 3px)', height: 'clamp(26px, 2.8vw, 46px)', background: '#009C3B', opacity: 0.85, flexShrink: 0, borderRadius: 2 }} />

      {/* ── Horas : Minutos : Segundos ── */}
      <div className="flex flex-col" style={{ gap: 'clamp(4px, 0.5vw, 10px)' }}>

        {/* Label topo */}
        <span style={{
          fontSize:      'clamp(7px, 0.6vw, 11px)',
          color:         'rgba(255,255,255,0.65)',
          fontWeight:    700,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          textShadow:    outline(4),
        }}>
          Começa em
        </span>

        {/* Números */}
        <div className="flex items-baseline" style={{ gap: 'clamp(3px, 0.4vw, 8px)' }}>
          {[
            { v: restante.horas,    l: 'h' },
            { v: restante.minutos,  l: 'm' },
            { v: restante.segundos, l: 's' },
          ].map(({ v, l }, i) => (
            <span key={l} className="flex items-baseline" style={{ gap: 'clamp(1px, 0.2vw, 4px)' }}>
              {i > 0 && (
                <span style={{ fontSize: 'clamp(11px, 1.1vw, 19px)', color: 'rgba(255,255,255,0.3)', fontWeight: 700, textShadow: outline(4), lineHeight: 1 }}>:</span>
              )}
              <span
                className="font-black font-mono tabular-nums leading-none"
                style={{ fontSize: 'clamp(16px, 1.8vw, 30px)', color: '#ffffff', textShadow: glowBranco }}
              >
                {String(v).padStart(2, '0')}
              </span>
              <span style={{
                fontSize:      'clamp(7px, 0.6vw, 11px)',
                color:         'rgba(255,255,255,0.45)',
                fontWeight:    700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                textShadow:    outline(3),
                alignSelf:     'flex-end',
                marginBottom:  'clamp(3px, 0.35vw, 6px)',
              }}>
                {l}
              </span>
            </span>
          ))}
        </div>

        {/* Data */}
        <span style={{
          fontSize:      'clamp(7px, 0.58vw, 10px)',
          color:         'rgba(255,255,255,0.35)',
          fontWeight:    600,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          textShadow:    outline(3),
        }}>
          11 jun · Los Angeles
        </span>
      </div>
    </div>
  );
}
